import React, { useState } from 'react';
import { IconChevronDown, IconDeviceLaptop, IconExternalLink, IconPencil } from '@tabler/icons-react';
import { checkInDeviceLabel } from '@techgitter/contracts';
import type { CheckInDraftJob, CheckInDraftJobSummary, DeviceModel } from '@techgitter/contracts';
import type { WorkItem } from '../workitems/workItemTypes';
import {
  useCheckInDeviceIdentities,
  formatDeviceIdentityParts,
  type CheckInDeviceIdentity,
} from '../../hooks/useCheckInDeviceIdentities';
import { useDeviceModels } from '../../hooks/useDeviceModels';
import { Chip } from '../chip';
import { CorrectSerialDialog } from '../check-in/device/CorrectSerialDialog';
import { formatAud } from '../check-in/sessionDetail/sessionDetailHelpers';
import { formatCheckAnswer, jobDeviceDetails } from '../../utils/jobDeviceDetails';
import { summariseDeviceModels } from '../../utils/deviceModelName';
// Reuses .panel-section-header (WorkItemPanel.css) + the .checkin-panel / device
// card styling so this matches the check-in device rows.
import './WorkItemPanel.css';
import './CheckInPanelSection.css';

interface JobDeviceSectionProps {
  workItem: WorkItem;
  /** Open the full check-in session detail view. */
  onOpenCheckInSession?: (sessionId: string) => void;
}

/**
 * Device details for a job created from a check-in. The originating device(s)
 * are attached to the job server-side as `checkInDraftJobs` (matched by RS
 * ticket id); make/model/serial are resolved live from the session. Renders
 * nothing for jobs with no linked check-in device.
 *
 * Each row shows the identity, the device the model number resolves to
 * (`/api/device-models`), and the item-status answers (what came in with the
 * machine). Everything else reception recorded — service notes, damage,
 * AI-read fields, inspection sign-off, photos, the model's specs — is behind
 * the row's disclosure chevron (`jobDeviceDetails`).
 */
export const JobDeviceSection: React.FC<JobDeviceSectionProps> = ({
  workItem,
  onOpenCheckInSession,
}) => {
  const {
    identities,
    draftJobs: fullDraftJobs,
    intakeType,
    refresh,
  } = useCheckInDeviceIdentities(workItem);
  // The device whose serial is being corrected, if any. Held by id so the
  // dialog always reads the freshest copy after a refetch.
  const [correctingJobId, setCorrectingJobId] = useState<string | null>(null);
  const draftJobs = workItem.checkInDraftJobs ?? [];
  const correcting = correctingJobId ? fullDraftJobs[correctingJobId] : undefined;
  if (draftJobs.length === 0) return null;
  const isExpress = intakeType === 'express';

  // Rendered as the first section inside the job-log card (Card B) — a header
  // band matching "Job log" plus the device rows, not a standalone card.
  return (
    <>
      <div className="panel-section-header">
        <IconDeviceLaptop className="panel-section-header__icon" size={18} stroke={1.5} />
        <span className="panel-section-header__title">
          {draftJobs.length > 1 ? 'Devices' : 'Device'}
        </span>
        {onOpenCheckInSession && workItem.checkInSessionGuid && (
          <button
            type="button"
            className="checkin-btn checkin-btn--primary checkin-btn--sm"
            onClick={() => onOpenCheckInSession(workItem.checkInSessionGuid!)}
          >
            <IconExternalLink size={14} stroke={1.5} />
            Check-In
          </button>
        )}
      </div>

      <div className="checkin-panel__body">
        <div className="checkin-panel__devices">
          {draftJobs.map(dj => (
            <JobDeviceRow
              key={dj.id}
              summary={dj}
              full={fullDraftJobs[dj.id]}
              identity={identities[dj.id]}
              sessionId={workItem.checkInSessionGuid ?? null}
              isExpress={isExpress}
              onCorrectSerial={() => setCorrectingJobId(dj.id)}
            />
          ))}
        </div>
      </div>

      {correcting && workItem.checkInSessionGuid && (
        <CorrectSerialDialog
          open
          sessionId={workItem.checkInSessionGuid}
          draftJob={correcting}
          onClose={() => setCorrectingJobId(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
};

interface JobDeviceRowProps {
  summary: CheckInDraftJobSummary;
  /** The session's copy; undefined until the session has loaded. */
  full: CheckInDraftJob | undefined;
  identity: CheckInDeviceIdentity | null | undefined;
  sessionId: string | null;
  isExpress: boolean;
  onCorrectSerial: () => void;
}

/** One device: its own component so the model lookup can be a hook. */
const JobDeviceRow: React.FC<JobDeviceRowProps> = ({
  summary: dj,
  full,
  identity,
  sessionId,
  isExpress,
  onCorrectSerial,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { data: models } = useDeviceModels(identity?.modelNumber, identity?.manufacturer);
  const modelName = summariseDeviceModels(models);

  const identityParts = formatDeviceIdentityParts(identity, dj.deviceType);
  // Only offered once the session has loaded and the device has actually been
  // inspected — there's no serial to correct before that.
  const canCorrectSerial = !!full?.inspection;
  const serialLabel = dj.deviceType === 'phone' ? 'IMEI' : 'serial';
  const details = full ? jobDeviceDetails(full) : null;
  const hasDetail =
    models.length > 0 || (!!details && (details.fields.length > 0 || details.photoCount > 0));
  const isOpen = hasDetail && expanded;
  const detailId = `job-device-detail-${dj.id}`;

  return (
    <div className="job-device">
      <div className="checkin-device checkin-device--plain">
        <Chip tone="neutral">{checkInDeviceLabel(dj)}</Chip>
        <span
          className="checkin-device__identity checkin-device__identity--inline"
          title={identityParts.join(' · ')}
        >
          {identityParts.join(' · ')}
        </span>
        {canCorrectSerial && (
          <button
            type="button"
            className="checkin-device__serial-edit"
            onClick={onCorrectSerial}
            title={`Correct ${serialLabel}`}
            aria-label={`Correct ${serialLabel}`}
          >
            <IconPencil size={13} stroke={1.5} />
          </button>
        )}
        {hasDetail && (
          <button
            type="button"
            className="job-device__toggle"
            onClick={() => setExpanded(v => !v)}
            aria-expanded={isOpen}
            aria-controls={detailId}
            aria-label={isOpen ? 'Hide device details' : 'Show device details'}
            title={isOpen ? 'Hide device details' : 'Show device details'}
          >
            <IconChevronDown
              size={16}
              stroke={1.75}
              className={isOpen ? 'job-device__chevron job-device__chevron--open' : 'job-device__chevron'}
            />
          </button>
        )}
        <span className="checkin-device__charge">
          {isExpress ? 'Express' : formatAud(dj.chargesTotal ?? 0)}
        </span>
      </div>

      {modelName && (
        <div className="job-device__model" title={models.map(m => m.name).join('\n')}>
          {modelName}
        </div>
      )}

      {details && details.itemStatus.length > 0 && (
        <div className="job-device__status">
          <span className="job-device__status-label">Item status</span>
          {details.itemStatus.map(formatCheckAnswer).join(' · ')}
        </div>
      )}

      {isOpen && (
        <dl className="job-device__detail" id={detailId}>
          {models.length > 0 && (
            <>
              <dt>{models.length > 1 ? `Model (one of ${models.length})` : 'Model'}</dt>
              <dd>
                {models.map(m => (
                  <div className="job-device__candidate" key={m.id}>
                    <span className="job-device__candidate-name">{m.name}</span>
                    {m.specSummary && <span>{m.specSummary}</span>}
                    <ModelLinks model={m} />
                  </div>
                ))}
              </dd>
            </>
          )}
          {details?.fields.map(f => (
            <React.Fragment key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </React.Fragment>
          ))}
          {details && details.photoCount > 0 && sessionId && (
            <>
              <dt>Photos</dt>
              <dd>
                {/* New tab, like every in-app link on the card, so the
                    expanded card survives. */}
                <a
                  href={`/check-in/session/${sessionId}/job/${dj.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {details.photoCount} {details.photoCount === 1 ? 'photo' : 'photos'}
                  <IconExternalLink size={12} stroke={1.5} />
                </a>
              </dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
};

/** The model row's external pages; renders nothing when it has none. */
const ModelLinks: React.FC<{ model: DeviceModel }> = ({ model }) => {
  const links = [
    { label: 'Specs', href: model.specsUrl },
    { label: 'Parts', href: model.partsUrl },
    { label: 'Service guide', href: model.serviceGuideUrl },
  ].filter((l): l is { label: string; href: string } => !!l.href);
  if (links.length === 0) return null;
  return (
    <span className="job-device__candidate-links">
      {links.map(l => (
        <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer">
          {l.label}
          <IconExternalLink size={12} stroke={1.5} />
        </a>
      ))}
    </span>
  );
};
